/**
 * useTokenRefresh - Automatisk token refresh före expiry
 * 
 * Detta hook sätter upp en timer som förnyar access token 5 minuter
 * innan den går ut. Detta säkerställer att användaren aldrig loggas
 * ut på grund av utgången token.
 * 
 * API:t utfärdar en ny 30-dagars app-refresh-token efter lyckad refresh.
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/auth-store';

const REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minuter innan expiry
const RETRY_DELAY_MS = 30 * 1000;

export function useTokenRefresh() {
  const { expiresAt, isAuthenticated, refreshAccessToken } = useAuthStore();
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    // Rensa tidigare timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Om inte autentiserad eller ingen expiry, gör inget
    if (!isAuthenticated || !expiresAt) {
      return;
    }

    let cancelled = false;
    const refresh = async () => {
      const result = await refreshAccessToken();
      if (result === 'retry' && !cancelled) {
        timerRef.current = window.setTimeout(() => {
          void refresh();
        }, RETRY_DELAY_MS);
      }
    };

    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;
    const timeUntilRefresh = timeUntilExpiry - REFRESH_BUFFER_MS;

    // Om token redan gått ut eller går ut inom 5 min, refresh omedelbart
    if (timeUntilRefresh <= 0) {
      void refresh();
    } else {
      timerRef.current = window.setTimeout(() => {
        void refresh();
      }, timeUntilRefresh);
    }

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [expiresAt, isAuthenticated, refreshAccessToken]);
}
